import { useState } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import type { ParcelCard, RouterOutputs } from 'shared/api'
import { handoverCancelReasonKey, trpcClient } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { Label } from 'shared/components/ui/label'
import { Textarea } from 'shared/components/ui/textarea'

/**
 * Cancelling a handover, on the parcel detail (ADR-0032, spec §15). The Parcel
 * was handed over by mistake and came back: it goes back on its Point's shelf and
 * can be handed over again by its scan. The payment stands — cancelling the cash
 * is its own act, once the Parcel is on the shelf.
 *
 * Who may cancel, and when, is the server's answer (`verdict`, from the parcel
 * detail): the operator within their Shift, support after it. The panel offers
 * the act only when the server allows it, and otherwise names why not. The reason
 * is required. The card shows the form in a dialog, the reason why not in its «⋯» menu.
 * The cancel call is injected (`cancel`) so it is the stubbed tRPC
 * seam in tests. Every string goes through the i18n lookup.
 */

type CancelResult = RouterOutputs['handover']['Cancel']
type ParcelDetail = ParcelCard
type Verdict = ParcelDetail['corrections']['cancelHandover']

async function cancelViaTrpc(input: { parcelId: string; reason: string }): Promise<CancelResult> {
	return trpcClient.handover.Cancel.mutate(input)
}

export interface HandoverCancelPanelProps {
	parcelId: string
	/** The Parcel's stage — the panel shows only while it is handed over. */
	state: ParcelDetail['state']
	/** Whether the caller may cancel the handover now, and why not (the server's). */
	verdict: Verdict
	/** Test seam: the cancel call. Defaults to the real tRPC client. */
	cancel?: (input: { parcelId: string; reason: string }) => Promise<CancelResult>
	/** Called after a successful cancellation, so the detail can refetch. */
	onCancelled?: (result: CancelResult) => void
	/** «Не отменять» — closes the dialog the form sits in. */
	onClose?: () => void
}

export function HandoverCancelPanel({
	parcelId,
	state,
	verdict,
	cancel = cancelViaTrpc,
	onCancelled,
	onClose,
}: HandoverCancelPanelProps) {
	const { t } = useTranslate()
	const [reason, setReason] = useState('')
	const [busy, setBusy] = useState(false)
	// The i18n KEY of the current error, not its text.
	const [errorKey, setErrorKey] = useState<string | null>(null)

	// Offered only for a handed-over Parcel, and not at all to a role that never
	// cancels (a cashier, an admin).
	if (state !== 'HANDED_OVER' || verdict.reason === 'not_permitted') return null

	const trimmed = reason.trim()

	const submit = async () => {
		if (!trimmed) {
			setErrorKey('handoverCancel.reasonRequired')
			return
		}
		setBusy(true)
		setErrorKey(null)
		try {
			const result = await cancel({ parcelId, reason: trimmed })
			if (result.outcome === 'refused') {
				setErrorKey(handoverCancelReasonKey(result.reason))
				return
			}
			onCancelled?.(result)
		} catch {
			setErrorKey('handoverCancel.failed')
		} finally {
			setBusy(false)
		}
	}

	if (!verdict.allowed && verdict.reason) {
		return (
			<p
				className='text-sm text-muted-foreground'
				data-testid='handover-cancel-unavailable'
				data-reason={verdict.reason}
			>
				{t(handoverCancelReasonKey(verdict.reason) as never)}
			</p>
		)
	}

	return (
		<div className='grid gap-4' data-testid='handover-cancel-panel'>
			<div className='grid gap-1.5'>
				<Label htmlFor='handover-cancel-reason'>{t('handoverCancel.reason')}</Label>
				<Textarea
					id='handover-cancel-reason'
					data-testid='handover-cancel-reason'
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					placeholder={t('handoverCancel.reasonPlaceholder')}
				/>
			</div>
			{errorKey && (
				<p className='text-sm text-destructive' data-testid='handover-cancel-error' data-key={errorKey}>
					{t(errorKey as never)}
				</p>
			)}
			<div className='flex justify-end gap-2'>
				{onClose && (
					<Button type='button' variant='outline' disabled={busy} onClick={onClose}>
						{t('handoverCancel.back')}
					</Button>
				)}
				<Button
					type='button'
					variant='destructive'
					data-testid='handover-cancel-submit'
					disabled={busy || !trimmed}
					onClick={submit}
				>
					{t('handoverCancel.submit')}
				</Button>
			</div>
		</div>
	)
}
